
import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { motion, easeOut } from "framer-motion";
import Classes from "./SectionA.module.css";
import '../Home.css';

import { LazyLoadImage } from 'react-lazy-load-image-component';
import 'react-lazy-load-image-component/src/effects/blur.css';
import LogoLoop from '../../../../utils/ReactUI/LogoLoop/LogoLoop';
import SparklesIcon from '../../../../utils/Icons';
import SplitText from '../../../../utils/ReactUI/SplitText/SplitText';

function SectionA() {
  const [width, setWidth] = useState(window.innerWidth);
  const { user } = useSelector((state) => state.auth);
  const navigate = useNavigate();

  const navigateToSignup = () => navigate('/signup/manufacturer');

  const updateDimensions = () => setWidth(window.innerWidth);
  useEffect(() => {
    window.addEventListener('resize', updateDimensions);
    return () => window.removeEventListener('resize', updateDimensions);
  }, []);

  // Responsive tuning for LogoLoop based on screen width
  const loopConfig = useMemo(() => {
    if (width <= 400) {
      return { logoHeight: 24, gap: 18, fadeOutColor: '#00000000' };
    } else if (width <= 480) {
      return { logoHeight: 28, gap: 22, fadeOutColor: '#00000000' };
    } else if (width <= 769) {
      return { logoHeight: 34, gap: 28, fadeOutColor: '#00000000' };
    } else if (width <= 1500) {
      return { logoHeight: 40, gap: 36, fadeOutColor: '#00000000' };
    }
    return { logoHeight: 48, gap: 50, fadeOutColor: '#ccf2ed' };
  }, [width]);

  const imageLogos = [
    { src: "/assets/images/company1.png", alt: "Company 1", href: "https://evolvion.io" },
    { src: "/assets/images/company2.png", alt: "Company 2", href: "https://evolvion.io" },
    { src: "/assets/images/company3.png", alt: "Company 3", href: "https://evolvion.io" },
    { src: "/assets/images/company4.png", alt: "Company 4", href: "https://evolvion.io" },
    { src: "/assets/images/company5.png", alt: "Company 5", href: "https://evolvion.io" },
  ];

  const handleAnimationComplete = () => {
    // console.log('All letters have animated!');
  };

  return (
    <>
      <section id="home" className={`${Classes.home} layoutPadding`}>
        <div className={Classes.container}>
          {/* Left — copy */}
          <motion.div
            className={Classes.colLeft}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0, transition: { duration: 0.5, ease: easeOut } }}
          >
            <div className='badge'>
              <SparklesIcon size={17} color='var(--secondary-color)' />
              Illuminate Your Creativity
            </div>

            <h1 className={Classes.title} >
              <SplitText
                text="Simplifying Lighting Specs"
                className="text-2xl font-semibold text-center"
                delay={5}
                duration={1.5}
                ease="elastic.out(1,0.3)"
                splitType="chars"
                from={{ opacity: 0, y: 40 }}
                to={{ opacity: 1, y: 0 }}
                threshold={0}
                rootMargin="-100px"
                textAlign="center"
                onLetterAnimationComplete={handleAnimationComplete}
              />
              <SplitText
                text="for Real-World Projects"
                className="text-2xl font-semibold text-center"
                delay={5}
                duration={1.5}
                ease="elastic.out(1,0.3)"
                splitType="chars"
                from={{ opacity: 0, y: 40 }}
                to={{ opacity: 1, y: 0 }}
                threshold={0}
                rootMargin="-100px"
                textAlign="center"
                onLetterAnimationComplete={handleAnimationComplete}
              />
            </h1>

            <p className={Classes.sub}>
              Harness the power of LightTrack to instantly search, filter, and export lighting fixture
              data — all from one clean, intuitive interface. Perfect for architecture, interior design,
              and 3D rendering workflows.
            </p>

            <div className={Classes.ctaRow}>
              <button className={Classes.primaryBtn} onClick={() => { navigate('/products') }} >
                Explore Lights
              </button>

              <button className={Classes.secondaryBtn} onClick={() => { navigate('/manufacturers') }} >
                For Manufacturers
              </button>
            </div>
          </motion.div>

          {/* Right — hero image */}
          <motion.div
            className={Classes.colRight}
            initial={{ opacity: 0, scale: 0.98 }}
            animate={{ opacity: 1, scale: 1, transition: { duration: 0.5, ease: easeOut, delay: 0.1 } }}
          >
            <div className={Classes.heroCard}>
              <LazyLoadImage
                effect="blur"
                className={Classes.heroImg}
                src="/assets/images/demo.png"
                alt="Lighting fixture drawings and samples on a desk"
                draggable={false}
              />
            </div>
          </motion.div>
        </div>

        {/* <p className={Classes.trusted}>
          Trusted by architects, lighting designers, and 3D artists in over 80 countries.
        </p>

        <div className={Classes.logoLoopCon} >
          <LogoLoop
            logos={imageLogos}
            speed={25}
            direction="left"
            logoHeight={loopConfig.logoHeight}
            gap={loopConfig.gap}
            pauseOnHover
            scaleOnHover
            fadeOut
            fadeOutColor={loopConfig.fadeOutColor}
            ariaLabel="Technology partners"
          />
        </div> */}

        <div className={Classes.pillRow} role="list">
          <div className={Classes.pill} role="listitem">
            <img src='/assets/icons/grid1.png' />
            Indoor/Outdoor
          </div>
          <div className={Classes.pill} role="listitem">
            <img src='/assets/icons/grid2.png' />
            Hanging / Ground
          </div>
          <div className={Classes.pill} role="listitem">
            <img src='/assets/icons/grid3.png' />
            Dimmable
          </div>
          <div className={Classes.pill} role="listitem">
            <img src='/assets/icons/grid4.png' />
            Color Temperature
          </div>
          <div className={Classes.pill} role="listitem">
            <img src='/assets/icons/grid5.png' />
            Wattage
          </div>
          <div className={Classes.pill} role="listitem">
            <img src='/assets/icons/grid6.png' />
            Beam Angle
          </div>
        </div>

        <img
          src="/assets/images/hero-wave.png"
          alt=""
          aria-hidden="true"
          className={Classes.authBg}
          draggable={false}
          loading="eager"
          decoding="async"
          fetchPriority="high"
        />
      </section>
    </>
  );
}

export default SectionA;


